/**
 * Design & Order page — self-serve product designer, rebuilt as a short wizard (see the plan this
 * was built from). A customer picks whether they're supplying their own garment, picks a product,
 * chooses print placements, uploads their own artwork, sees a live mockup, and submits.
 *
 * Small/printer-supplied jobs route to the instant print-on-demand flow (/print-order); everything
 * else becomes a QuoteRequest carrying the structured design data (staff price it as a SalesQuote,
 * customer approves/pays in their account, it becomes an Order in the Print Queue). See
 * docs/NOOSA-ORDER-INTAKE-TASKS.md Tasks 7/8/9/11.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortal } from '../context/PortalContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import GarmentMockup, { PLACEMENT_ZONES } from '../components/GarmentMockup';
import WizardStep from '../components/WizardStep';
import OptionCard from '../components/OptionCard';
import SuccessState from '../components/SuccessState';
import { submitProductQuoteRequest, uploadDesignArtwork } from '../services/api';
import { INSTANT_ORDER_MAX_QUANTITY } from '../config/quoting';
import { ACCEPTED_ARTWORK_ACCEPT_ATTR, ACCEPTED_ARTWORK_LABEL, validateArtworkFile } from '../config/artwork';

const PLACEMENTS = [
  { key: 'front', label: 'Front' },
  { key: 'back', label: 'Back' },
  { key: 'left_sleeve', label: 'Left Sleeve' },
  { key: 'right_sleeve', label: 'Right Sleeve' },
];

// Not every product has a front, a back, and two sleeves to print on — a cap, tote bag, or
// stubby cooler doesn't have sleeves at all, and offering them let customers pick a placement
// that doesn't exist on the actual garment. Until products carry their own print-area list
// (a real fix — see the note in the placement step below), fall back to category: only
// 'apparel' gets sleeve options, everything else gets front/back only.
function getAvailablePlacements(product) {
  if (product?.category === 'apparel') return PLACEMENTS;
  return PLACEMENTS.filter((p) => p.key === 'front' || p.key === 'back');
}

const STEPS = ['garment', 'design', 'details'];
const STEP_LABELS = { garment: 'Garment', design: 'Design', details: 'Your details' };

export default function DesignOrderPage() {
  const { products, portal, loading } = usePortal();
  const businessId = portal?.business?.id;
  const navigate = useNavigate();

  const [step, setStep] = useState('garment');
  const [garmentSource, setGarmentSource] = useState('printer_supplies');
  const [productId, setProductId] = useState('');
  const [selectedPlacements, setSelectedPlacements] = useState([]);
  const [artworkByPlacement, setArtworkByPlacement] = useState({}); // { [placement]: url }
  const [uploadingPlacement, setUploadingPlacement] = useState(null);
  const [quantity, setQuantity] = useState('20');
  const [notes, setNotes] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [orderingAsBusiness, setOrderingAsBusiness] = useState(false);
  const [businessMangoId, setBusinessMangoId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const selectedProduct = products.find((p) => p.id === productId);
  const availablePlacements = getAvailablePlacements(selectedProduct);
  const qtyNum = parseInt(quantity, 10) || 0;
  const willRouteToInstantOrder = garmentSource === 'printer_supplies' && qtyNum > 0 && qtyNum <= INSTANT_ORDER_MAX_QUANTITY;

  const togglePlacement = (key) => {
    setSelectedPlacements((prev) => (prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]));
  };

  const handleArtworkUpload = async (placementKey, file) => {
    if (!businessId) return;
    const validationError = validateArtworkFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setUploadingPlacement(placementKey);
    setError('');
    try {
      const url = await uploadDesignArtwork(file, businessId);
      setArtworkByPlacement((prev) => ({ ...prev, [placementKey]: url }));
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploadingPlacement(null);
    }
  };

  const goToDesign = () => setStep('design');
  const goToDetails = () => {
    if (!selectedProduct) {
      setError('Choose a product first.');
      return;
    }
    if (selectedPlacements.length === 0) {
      setError('Choose at least one print placement.');
      return;
    }
    const missingArt = selectedPlacements.filter((p) => !artworkByPlacement[p]);
    if (missingArt.length > 0) {
      setError('Upload artwork for every placement you selected.');
      return;
    }
    setError('');
    setStep('details');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError('Name and email are required.');
      return;
    }
    if (orderingAsBusiness && !businessMangoId.trim()) {
      setError('Enter your Mango Business ID, or turn off "ordering as a business".');
      return;
    }

    // Small, printer-supplied jobs skip the quote queue entirely — straight to an instant order.
    if (willRouteToInstantOrder) {
      navigate(`/print-order?quantity=${qtyNum}`);
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const placements = selectedPlacements.map((placement) => ({
        placement,
        artworkUrl: artworkByPlacement[placement],
      }));
      const placementLabels = selectedPlacements
        .map((k) => PLACEMENTS.find((p) => p.key === k)?.label || k)
        .join(', ');
      const jobDescriptionParts = [
        `${quantity || '?'}x ${selectedProduct.name}`,
        `custom design — placements: ${placementLabels}`,
        garmentSource === 'customer_supplies' ? 'customer will supply garment' : 'use printer stock',
      ];
      if (orderingAsBusiness) jobDescriptionParts.push(`ordering as business (Mango ID: ${businessMangoId.trim()})`);
      if (notes.trim()) jobDescriptionParts.push(notes.trim());

      await submitProductQuoteRequest(selectedProduct.id, {
        jobDescription: jobDescriptionParts.join(' — '),
        guestName: name.trim(),
        guestEmail: email.trim(),
        guestPhone: phone.trim() || undefined,
        designData: {
          placements,
          garmentSource,
          quantity: qtyNum || undefined,
          orderingBusinessMangoId: orderingAsBusiness ? businessMangoId.trim() : undefined,
        },
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong — please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <>
      <Header />
      <main className="section">
        <div className="container" style={{ maxWidth: 640 }}>
          {submitted ? (
            <SuccessState
              title="Design submitted"
              message={`We've got your design for ${selectedProduct?.name}. We'll email you a price once we've reviewed it.`}
              steps={['Submitted', 'We review', 'You get a price']}
            >
              <a href="/account/quotes" className="btn btn-primary">Track your quote</a>
            </SuccessState>
          ) : (
            <>
              <div className="masthead">
                <span className="eyebrow">Design your order</span>
                <h1>Let's build your print job</h1>
                <p>Pick a product, choose where your print goes, upload your artwork, and send it to us.</p>
              </div>

              {!submitting && (
                <div className="timeline" style={{ marginBottom: '32px' }}>
                  {STEPS.map((s, i) => {
                    const currentIndex = STEPS.indexOf(step);
                    const cls = i < currentIndex ? 'is-complete' : i === currentIndex ? 'is-current' : '';
                    return (
                      <div key={s} className={`timeline-step ${cls}`}>
                        <div className="timeline-node">{i < currentIndex ? '✓' : i + 1}</div>
                        <div className="timeline-label">{STEP_LABELS[s]}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {error && <div style={errorStyle}>{error}</div>}

              {step === 'garment' && (
                <WizardStep stepKey="garment">
                  <div className="section-heading" style={{ textAlign: 'left', margin: '0 0 16px' }}>
                    <h2 style={{ textAlign: 'left' }}>Where's the garment coming from?</h2>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
                    <OptionCard
                      title="Use your stock"
                      description="We supply the blank garment — pick one from our products."
                      selected={garmentSource === 'printer_supplies'}
                      onClick={() => setGarmentSource('printer_supplies')}
                    />
                    <OptionCard
                      title="I'll supply my own"
                      description="You already have the garment — we just print on it. No garment charge."
                      selected={garmentSource === 'customer_supplies'}
                      onClick={() => setGarmentSource('customer_supplies')}
                    />
                  </div>
                  <button type="button" onClick={goToDesign} className="btn btn-primary">Continue</button>
                </WizardStep>
              )}

              {step === 'design' && (
                <WizardStep stepKey="design">
                  <label style={labelStyle}>
                    {garmentSource === 'customer_supplies' ? 'Closest garment type (for print area only — no charge)' : 'Pick a product'}
                    <select
                      value={productId}
                      onChange={(e) => {
                        setProductId(e.target.value);
                        setSelectedPlacements([]);
                        setArtworkByPlacement({});
                      }}
                      style={inputStyle}
                      required
                    >
                      <option value="">Select a product…</option>
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </label>

                  {selectedProduct && (
                    <>
                      <div style={{ marginTop: '10px' }}>
                        <div style={stepLabelStyle}>Print placements</div>
                        <p style={helperTextStyle}>
                          Only placements that exist on this product are shown. Keep your artwork
                          within the dashed outline below.
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                          {availablePlacements.map((p) => (
                            <OptionCard
                              key={p.key}
                              title={p.label}
                              selected={selectedPlacements.includes(p.key)}
                              onClick={() => togglePlacement(p.key)}
                              style={{ flex: '0 1 140px' }}
                            />
                          ))}
                        </div>
                      </div>

                      {selectedPlacements.length > 0 && (
                        <div style={{ marginTop: '20px' }}>
                          <div style={stepLabelStyle}>Upload artwork & preview</div>
                          <div style={mockupGridStyle}>
                            {selectedPlacements.map((placement) => {
                              const label = PLACEMENTS.find((p) => p.key === placement)?.label;
                              const zone = PLACEMENT_ZONES[placement];
                              const ratio = zone ? `${zone.width}:${zone.height}` : null;
                              return (
                                <div key={placement} style={mockupCardStyle}>
                                  <GarmentMockup placement={placement} artworkUrl={artworkByPlacement[placement]} garmentSource={garmentSource} />
                                  <div style={{ fontSize: '13px', fontWeight: 600, margin: '10px 0 6px' }}>{label}</div>
                                  {ratio && (
                                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px', lineHeight: 1.4 }}>
                                      Print area ratio {ratio} — high-res artwork (300 DPI), transparent PNG works best.
                                      We accept {ACCEPTED_ARTWORK_LABEL.toLowerCase()}.
                                    </div>
                                  )}
                                  <input
                                    type="file"
                                    accept={ACCEPTED_ARTWORK_ACCEPT_ATTR}
                                    disabled={uploadingPlacement === placement}
                                    onChange={(e) => {
                                      const f = e.target.files?.[0];
                                      if (f) handleArtworkUpload(placement, f);
                                    }}
                                    style={{ fontSize: '12px', width: '100%' }}
                                  />
                                  {uploadingPlacement === placement && <div style={{ fontSize: '12px', color: '#888' }}>Uploading…</div>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <label style={{ ...labelStyle, marginTop: '20px', maxWidth: 200 }}>
                        Quantity
                        <input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} style={inputStyle} required />
                      </label>
                      {garmentSource === 'printer_supplies' && (
                        <p style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '-10px' }}>
                          {willRouteToInstantOrder
                            ? `${qtyNum} is a small enough run to order instantly — you'll get a live price on the next step.`
                            : `Runs over ${INSTANT_ORDER_MAX_QUANTITY} go through a custom quote so we can price it properly.`}
                        </p>
                      )}

                      <button type="button" onClick={goToDetails} className="btn btn-primary" style={{ marginTop: '8px' }}>Continue</button>
                    </>
                  )}
                </WizardStep>
              )}

              {step === 'details' && (
                <WizardStep stepKey="details">
                  <form onSubmit={handleSubmit}>
                    <label style={labelStyle}>
                      Notes
                      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Colours, sizes needed, deadline..." />
                    </label>

                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <label style={{ ...labelStyle, flex: '1 1 180px' }}>
                        Your name
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} required />
                      </label>
                      <label style={{ ...labelStyle, flex: '1 1 180px' }}>
                        Email
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} required />
                      </label>
                    </div>
                    <label style={labelStyle}>
                      Phone (optional)
                      <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} style={inputStyle} />
                    </label>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 600, color: '#444', marginBottom: '14px' }}>
                      <input type="checkbox" checked={orderingAsBusiness} onChange={(e) => setOrderingAsBusiness(e.target.checked)} />
                      I'm ordering as a registered business (wholesale/B2B)
                    </label>
                    {orderingAsBusiness && (
                      <label style={labelStyle}>
                        Your Mango Business ID
                        <input type="text" value={businessMangoId} onChange={(e) => setBusinessMangoId(e.target.value)} placeholder="e.g. MB-XX-123456" style={inputStyle} />
                      </label>
                    )}

                    <button type="submit" disabled={submitting} className="btn btn-primary" style={{ width: '100%', marginTop: '10px' }}>
                      {submitting ? 'Submitting…' : willRouteToInstantOrder ? 'Continue to instant order' : 'Submit Design'}
                    </button>
                  </form>
                </WizardStep>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}

const labelStyle = { display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#444', marginBottom: '14px' };
const inputStyle = { padding: '10px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', fontWeight: 400, fontFamily: 'inherit' };
const errorStyle = { background: '#fef2f2', color: '#ef4444', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' };
const stepLabelStyle = { fontSize: '14px', fontWeight: 600, color: '#333', marginBottom: '10px' };
const helperTextStyle = { fontSize: '12px', color: '#888', margin: '-4px 0 10px' };
const mockupGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px' };
const mockupCardStyle = { border: '1px solid #eee', borderRadius: '10px', padding: '12px', background: '#fafafa' };
